import React from 'react';
import {
  Box,
  Flex,
  HStack,
  Badge,
  Text,
  Heading,
  Grid,
  GridItem,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardHeader,
  CardBody,
  VStack
} from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';

const VarianceDetailsCard = ({ report }) => {
  if (!report) return null;

  const aiDiffs = report.cpt_ai_evaluation?.differences || [];
  const triageEval = report.cpt_ai_evaluation?.triage_evaluation;
  const varianceSeverity = report.cpt_ai_evaluation?.variance_severity;
  const holisticSummary = report.cpt_ai_evaluation?.holistic_summary;
  const { exact_matches, modifier_mismatches, unit_mismatches, missing_in_prospect, extra_in_prospect } = report.cpt_variance || {};

  // Determine status based on AI evaluation
  let isActionable = false;
  let isAutoResolved = false;
  let hasVariance = false;

  if (report.cpt_variance) {
    hasVariance = (
      (modifier_mismatches && modifier_mismatches.length > 0) ||
      (unit_mismatches && unit_mismatches.length > 0) ||
      (missing_in_prospect && missing_in_prospect.length > 0) ||
      (extra_in_prospect && extra_in_prospect.length > 0)
    );
  }

  if (hasVariance && triageEval) {
    if (triageEval.actionable_variance === true) {
      isActionable = true;
    } else if (triageEval.actionable_variance === false) {
      isAutoResolved = true;
    }
  }

  let cardBorderColor = "green.200";
  let cardBgColor = "green.50";
  let badgeColorScheme = "green";
  let badgeText = "Perfect Match";

  if (hasVariance) {
    if (isActionable) {
      cardBorderColor = "orange.200";
      cardBgColor = "orange.50";
      badgeColorScheme = "orange";
      badgeText = "Actionable Variance";
    } else if (isAutoResolved) {
      cardBorderColor = "blue.200";
      cardBgColor = "blue.50";
      badgeColorScheme = "blue";
      badgeText = "Auto-Resolved Variance";
    } else {
      // Fallback if there is a variance but no triage evaluation
      cardBorderColor = "yellow.400";
      cardBgColor = "yellow.50";
      badgeColorScheme = "yellow";
      badgeText = "Variance Detected";
    }
  }

  const renderModifiers = (modifiers) => {
    if (!modifiers || modifiers.length === 0) return <Text fontSize="xs" color="gray.400">None</Text>;
    return (
      <Flex flexWrap="wrap" gap={1}>
        {modifiers.map((mod, idx) => (
          <Badge key={idx} colorScheme="gray" variant="outline" fontSize="xs">{mod}</Badge>
        ))}
      </Flex>
    );
  };

  return (
    <Card mb={6} shadow="sm" borderWidth="1px" borderColor={cardBorderColor}>
      <CardHeader bg={cardBgColor} py={3} borderBottomWidth="1px">
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <Badge colorScheme={badgeColorScheme} fontSize="sm">
              {badgeText}
            </Badge>
            <Text fontWeight="bold" color="gray.800">{report.external_case_id}</Text>
            <Text color="gray.600" fontSize="sm">{report.payer_type} - {report.carrier_name}</Text>
            <Badge colorScheme={report.claim_type === 'tech' ? 'purple' : 'blue'}>
              {report.claim_type?.toUpperCase() || 'PRO'}
            </Badge>
          </HStack>
          <HStack>
            <Text fontSize="xs" color="gray.500">{new Date(report.created_at).toLocaleDateString()}</Text>
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <Grid templateColumns={{ base: "1fr", lg: "3fr 2fr" }} gap={8}>
          {/* Left Side: Deterministic Data */}
          <GridItem overflowX="auto">
            <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>Deterministic Variance</Heading>
            <Box borderWidth="1px" rounded="md" overflow="hidden">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th borderRightWidth="1px"></Th>
                    <Th colSpan={3} textAlign="center" borderRightWidth="1px" bg="gray.100">Claim Data (CMS 1500)</Th>
                    <Th colSpan={3} textAlign="center" bg="blue.50">Revedy Output</Th>
                  </Tr>
                  <Tr>
                    <Th>CPT</Th>
                    <Th>Modifiers</Th>
                    <Th borderRightWidth="1px">Units</Th>
                    <Th bg="blue.50">CPT</Th>
                    <Th bg="blue.50">Modifiers</Th>
                    <Th bg="blue.50">Units</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {/* Exact Matches */}
                  {exact_matches?.map((item, idx) => (
                    <Tr key={`exact-${idx}`} bg="green.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="green">Match</Badge></Td>
                      <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                      <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                      <Td borderRightWidth="1px">{item.prospect.units}</Td>
                      <Td fontWeight="bold">{item.revedy.cpt_code}</Td>
                      <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                      <Td>{item.revedy.units}</Td>
                    </Tr>
                  ))}

                  {/* Modifier Mismatches */}
                  {modifier_mismatches?.map((item, idx) => (
                    <Tr key={`mod-${idx}`} bg="yellow.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="yellow">Mod Diff</Badge></Td>
                      <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                      <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                      <Td borderRightWidth="1px">{item.prospect.units}</Td>
                      <Td fontWeight="bold">{item.revedy.cpt_code}</Td>
                      <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                      <Td>{item.revedy.units}</Td>
                    </Tr>
                  ))}

                  {/* Unit Mismatches */}
                  {unit_mismatches?.map((item, idx) => (
                    <Tr key={`unit-${idx}`} bg="orange.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="orange">Unit Diff</Badge></Td>
                      <Td fontWeight="bold">{item.prospect.cpt_code}</Td>
                      <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                      <Td borderRightWidth="1px" color="red.600" fontWeight="bold">{item.prospect.units}</Td>
                      <Td fontWeight="bold">{item.revedy.cpt_code}</Td>
                      <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                      <Td color="green.600" fontWeight="bold">{item.revedy.units}</Td>
                    </Tr>
                  ))}

                  {/* Missing in Prospect (Revedy found it, Prospect didn't) */}
                  {missing_in_prospect?.map((item, idx) => (
                    <Tr key={`miss-${idx}`} bg="red.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="red">Missing</Badge></Td>
                      <Td color="gray.400" fontStyle="italic">Not Billed</Td>
                      <Td>-</Td>
                      <Td borderRightWidth="1px">-</Td>
                      <Td fontWeight="bold" color="green.600">{item.revedy.cpt_code}</Td>
                      <Td>{renderModifiers(item.revedy.modifiers)}</Td>
                      <Td>{item.revedy.units}</Td>
                    </Tr>
                  ))}

                  {/* Extra in Prospect (Prospect billed it, Revedy didn't) */}
                  {extra_in_prospect?.map((item, idx) => (
                    <Tr key={`extra-${idx}`} bg="purple.50">
                      <Td borderRightWidth="1px"><Badge colorScheme="purple">Extra</Badge></Td>
                      <Td fontWeight="bold" color="red.600">{item.prospect.cpt_code}</Td>
                      <Td>{renderModifiers(item.prospect.modifiers)}</Td>
                      <Td borderRightWidth="1px">{item.prospect.units}</Td>
                      <Td color="gray.400" fontStyle="italic">Not Recommended</Td>
                      <Td>-</Td>
                      <Td>-</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            {report.revedy_cpt_data && report.revedy_cpt_data.length > 0 && (
              <Box mt={6}>
                <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>Revedy Code Justifications</Heading>
                <Box borderWidth="1px" rounded="md" overflow="hidden" bg="white">
                  <Table variant="simple" size="sm">
                    <Thead bg="gray.50">
                      <Tr>
                        <Th width="100px">CPT</Th>
                        <Th>Clinical Justification</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {report.revedy_cpt_data.map((cpt, idx) => (
                        <Tr key={idx}>
                          <Td fontWeight="bold" verticalAlign="top">{cpt.cpt_code}</Td>
                          <Td>
                            <Text fontSize="sm" color="gray.800" mb={1}>{cpt.justification || 'No specific justification provided.'}</Text>
                            {cpt.long_description && (
                              <Text fontSize="xs" color="gray.500" fontStyle="italic">{cpt.long_description}</Text>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </Box>
            )}
          </GridItem>

          {/* Right Side: AI Evaluation */}
          <GridItem>
            <Heading size="xs" color="gray.500" textTransform="uppercase" mb={3}>AI Evaluation</Heading>
            <VStack align="stretch" spacing={4}>
              {triageEval && (
                <Box p={3} bg={isActionable ? "orange.50" : "blue.50"} rounded="md" borderWidth="1px" borderColor={isActionable ? "orange.200" : "blue.200"}>
                  <Text fontWeight="bold" color={isActionable ? "orange.800" : "blue.800"} mb={1}>
                    {isActionable ? "Actionable Variance Detected" : "Auto-Resolved by AI"}
                  </Text>
                  <Text fontSize="sm" color={isActionable ? "orange.900" : "blue.900"}>
                    {triageEval.reasoning}
                  </Text>
                </Box>
              )}

              {(varianceSeverity || holisticSummary) && (
                <Box p={3} bg="purple.50" rounded="md" borderWidth="1px" borderColor="purple.200">
                  {varianceSeverity && (
                    <Text fontSize="sm" fontWeight="bold" color="purple.800" mb={1}>
                      Severity: {varianceSeverity}
                    </Text>
                  )}
                  {holisticSummary && (
                    <Text fontSize="sm" color="purple.900">
                      <Text as="span" fontWeight="bold">Summary: </Text>
                      {holisticSummary}
                    </Text>
                  )}
                </Box>
              )}

              {aiDiffs.length > 0 ? (
                aiDiffs.map((diff, idx) => (
                  <Box key={idx} p={3} bg="gray.50" rounded="md" borderWidth="1px" borderColor="gray.200">
                    <Flex justify="space-between" mb={2}>
                      <Box>
                        <Text fontSize="2xs" fontWeight="bold" color="gray.500">CLAIM</Text>
                        <Text fontSize="sm" fontWeight="medium">{diff.claim_cpt || diff.cpt_code || 'Unknown CPT'}</Text>
                      </Box>
                      <Box textAlign="right">
                        <Text fontSize="2xs" fontWeight="bold" color="gray.500">REVEDY</Text>
                        <Text fontSize="sm" fontWeight="medium">{diff.revedy_cpt || 'Unknown CPT'}</Text>
                      </Box>
                    </Flex>
                    <Flex align="center" mb={2} gap={2}>
                      {diff.who_was_right && (
                        <Badge 
                          colorScheme={
                            diff.who_was_right?.toLowerCase() === 'revedy' ? 'blue' : 
                            (diff.who_was_right?.toLowerCase() === 'claim' || diff.who_was_right?.toLowerCase() === 'prospect') ? 'orange' : 
                            diff.who_was_right?.toLowerCase() === 'both' ? 'green' : 'red'
                          }
                          fontSize="2xs"
                        >
                          RIGHT: {diff.who_was_right?.toUpperCase()}
                        </Badge>
                      )}
                      {diff.variance_type && (
                        <Badge colorScheme={diff.variance_type === 'missing_in_prospect' ? 'red' : 'orange'} variant="outline" fontSize="2xs">
                          {diff.variance_type.replace(/_/g, ' ')}
                        </Badge>
                      )}
                    </Flex>
                    <Box fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                      <ReactMarkdown>{diff.ai_reasoning || diff.explanation}</ReactMarkdown>
                    </Box>
                  </Box>
                ))
              ) : (
                <Text fontSize="sm" color="gray.500" fontStyle="italic">
                  {hasVariance ? "No specific AI reasoning provided for these variances." : "No variances detected. Codes match perfectly."}
                </Text>
              )}
            </VStack>
          </GridItem>
        </Grid>
      </CardBody>
    </Card>
  );
};

export default VarianceDetailsCard;
